import { MoneyV2 } from '@/__generated__/storefront/graphql';
import {
  carouselCollectionByHandleQuery,
  carouselProductByHandleQuery
} from '@/shared/graphql/storefront/queries/collection.queries';
import { getSharedStorefrontClient } from '@/shared/utils/apollo-client';
import { getCountryCode } from '@/shared/utils/country';
import { getGlobalTranslation, setupI18n } from '@/shared/utils/i18n';
import {
  formatPriceStorefront,
  isCompareAtPriceHigher
} from '@/shared/utils/shopify/money-formatter';

const carouselResolveDataset = {
  PENDING_CLASS: 'js-carousel-pending',
  IMAGE_SLOT_CLASS: 'js-carousel-image-slot',
  TITLE_SLOT_CLASS: 'js-carousel-title-slot',
  DETAILS_SLOT_CLASS: 'js-carousel-details-slot'
} as const;

const entityTypes = {
  PRODUCT: 'product',
  PATTERN: 'pattern'
} as const;

type MetafieldValue = { value: string } | null | undefined;
type VariantPrice = {
  price: MoneyV2;
  compareAtPrice?: MoneyV2 | null;
};
type CarouselProduct = {
  title: string;
  featuredImage?: { url: string; altText?: string | null } | null;
  entityTypeMetafield?: MetafieldValue;
  brandMetafield?: MetafieldValue;
  subtitleMetafield?: MetafieldValue;
  designerMetafield?: MetafieldValue;
  patternKindMetafield?: MetafieldValue;
  yarnWeightCategoryMetafield?: MetafieldValue;
  variants?: { nodes: VariantPrice[] };
};

const metafieldValue = (metafield: MetafieldValue): string =>
  metafield?.value ?? '';

// Mirrors the Liquid title-resolution order: manual_label always wins
// (snippets/product-capture-title.liquid only runs when it's blank).
const resolveTitle = (
  product: CarouselProduct,
  manualLabel: string
): string => {
  // Mirrors Liquid's `manual_label == blank` — whitespace-only counts as
  // blank too, so it falls back to the product title like Liquid does.
  if (manualLabel.trim()) return manualLabel;
  const entityType = metafieldValue(product.entityTypeMetafield);
  const brand = metafieldValue(product.brandMetafield);
  if (entityType === entityTypes.PRODUCT && brand) {
    return `${product.title} - ${brand}`;
  }
  return product.title;
};

// Mirrors snippets/product-capture-subtitle.liquid
const resolveSubtitle = (product: CarouselProduct): string => {
  const entityType = metafieldValue(product.entityTypeMetafield);
  if (entityType === entityTypes.PRODUCT) {
    return metafieldValue(product.subtitleMetafield);
  }
  if (entityType === entityTypes.PATTERN) {
    return metafieldValue(product.designerMetafield);
  }
  return '';
};

// Mirrors Liquid's `| capitalize` filter: first character uppercased, the
// rest lowercased (not just left as-is).
const capitalize = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

// Mirrors snippets/product-capture-subtitle-tag.liquid + product-capture-yarn-weight-category.liquid
const resolveTag = (product: CarouselProduct): string => {
  const entityType = metafieldValue(product.entityTypeMetafield);
  if (entityType === entityTypes.PATTERN) {
    return capitalize(metafieldValue(product.patternKindMetafield));
  }
  if (entityType === entityTypes.PRODUCT) {
    const yarnWeight = metafieldValue(product.yarnWeightCategoryMetafield);
    if (!yarnWeight) return '';
    const translate = getGlobalTranslation() as (key: string) => string;
    return translate(`global.yarn_weight.${yarnWeight}`);
  }
  return '';
};

// Approximates snippets/card-product-price.liquid's cheapest-variant selection
// (selected_or_first_available_variant, overridden to the cheapest variant at
// price_min when price varies). This lightweight fallback query only carries
// price/compareAtPrice per variant, so free/loyalty-point pricing isn't
// replicated here — acceptable for the rare overflow-item tail this covers.
const cheapestVariant = (
  variants: VariantPrice[]
): VariantPrice | undefined => {
  if (!variants.length) return undefined;
  const minPrice = Math.min(...variants.map((v) => parseFloat(v.price.amount)));
  const candidates = variants.filter(
    (v) => parseFloat(v.price.amount) === minPrice
  );
  return candidates.reduce((cheapest, candidate) => {
    const cheapestCompareAt = parseFloat(
      cheapest.compareAtPrice?.amount ?? '0'
    );
    const candidateCompareAt = parseFloat(
      candidate.compareAtPrice?.amount ?? '0'
    );
    return candidateCompareAt > cheapestCompareAt ? candidate : cheapest;
  }, candidates[0]);
};

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[char] ?? char
  );

// Mirrors snippets/card-product-subtitle.liquid
const renderSubtitleHtml = (subtitle: string, tag: string): string => {
  if (!subtitle && !tag) return '';
  const tagHtml = tag
    ? `<span class="mr-1 rounded bg-gray-100 px-1 py-px leading-none text-gray-600">${escapeHtml(tag)}</span>`
    : '';
  return `<div class="text-xs leading-snug text-gray-500">${tagHtml}${escapeHtml(subtitle)}</div>`;
};

// Mirrors Liquid's `product.price_varies`: true when more than one distinct
// price exists across variants.
const priceVaries = (variants: VariantPrice[]): boolean => {
  if (variants.length < 2) return false;
  const firstAmount = variants[0].price.amount;
  return variants.some((v) => v.price.amount !== firstAmount);
};

// Mirrors snippets/card-product-price.liquid's discount/non-discount branches
const renderPriceHtml = (
  variant: VariantPrice,
  showFromPrefix: boolean
): string => {
  const price = formatPriceStorefront(variant.price);
  const translate = getGlobalTranslation() as (key: string) => string;
  const fromPrefix = showFromPrefix
    ? `<span>${escapeHtml(translate('price.text_from_price'))}</span> `
    : '';
  if (!isCompareAtPriceHigher(variant.price, variant.compareAtPrice)) {
    return `<div class="whitespace-nowrap">${fromPrefix}<span class="whitespace-nowrap">${price}</span></div>`;
  }
  const compareAtPrice = formatPriceStorefront(
    variant.compareAtPrice ?? undefined
  );
  const salePriceLabel = escapeHtml(translate('price.sale_price'));
  const originalPriceLabel = escapeHtml(translate('price.original_price'));
  return (
    '<div class="whitespace-nowrap">' +
    fromPrefix +
    '<div class="inline-flex justify-start gap-3.5">' +
    '<span class="whitespace-nowrap font-bold text-pink">' +
    `<span class="sr-only">${salePriceLabel} </span>${price}` +
    '<span class="sr-only">, </span></span>' +
    '<span class="whitespace-nowrap stroke-pink text-gray-300 line-through">' +
    `<span class="sr-only">${originalPriceLabel} </span>${compareAtPrice}` +
    '</span></div></div>'
  );
};

// Mirrors the skeleton shimmer classes on .js-carousel-image-slot in
// sections/category-carousel.liquid — must be removed once resolved, since
// they paint a `::before` overlay on the slot itself, not on its children,
// so replaceChildren() alone never clears them.
const SHIMMER_CLASSES = [
  'bg-gray-100/20',
  'before:absolute',
  'before:inset-0',
  'before:-translate-x-full',
  'before:animate-[shimmer_2s_infinite]',
  'before:bg-gradient-to-r',
  'before:from-transparent',
  'before:via-gray-200/30',
  'before:to-transparent',
  "before:content-['']"
];

// Server-rendered slides go through snippets/image.liquid, which resizes via
// Shopify's `image_url: width: N` filter — the CDN honors the same `width`
// query param directly, so requesting it here keeps this fallback path from
// pulling full-resolution originals for a ~200px-wide thumbnail.
const CAROUSEL_IMAGE_WIDTH = 400;

const sizedImageUrl = (url: string, width: number): string => {
  try {
    const resized = new URL(url);
    resized.searchParams.set('width', String(width));
    return resized.toString();
  } catch {
    return url;
  }
};

const populateImage = (
  slideEl: HTMLElement,
  url: string,
  alt: string
): void => {
  const slot = slideEl.querySelector<HTMLElement>(
    `.${carouselResolveDataset.IMAGE_SLOT_CLASS}`
  );
  if (!slot) return;
  const img = document.createElement('img');
  img.src = sizedImageUrl(url, CAROUSEL_IMAGE_WIDTH);
  img.alt = alt;
  img.loading = 'lazy';
  img.className = 'h-full w-full object-cover';
  slot.classList.remove(...SHIMMER_CLASSES);
  slot.replaceChildren(img);
};

const populateTitle = (slideEl: HTMLElement, title: string): void => {
  const slot = slideEl.querySelector<HTMLElement>(
    `.${carouselResolveDataset.TITLE_SLOT_CLASS}`
  );
  if (slot) slot.textContent = title;
};

const populateDetails = (
  slideEl: HTMLElement,
  product: CarouselProduct
): void => {
  const slot = slideEl.querySelector<HTMLElement>(
    `.${carouselResolveDataset.DETAILS_SLOT_CLASS}`
  );
  if (!slot) return;

  const subtitleHtml = renderSubtitleHtml(
    resolveSubtitle(product),
    resolveTag(product)
  );
  const variants = product.variants?.nodes ?? [];
  const variant = cheapestVariant(variants);
  const priceHtml = variant
    ? renderPriceHtml(variant, priceVaries(variants))
    : '';

  slot.innerHTML = subtitleHtml + priceHtml;
};

/**
 * Resolves a category-carousel slide left blank by the server-side
 * all_products[]/collections[] lookup (Shopify caps that lookup at 20 unique
 * handles per page render). Returns true if the slide was populated, false if
 * the underlying product/collection no longer resolves and the slide should
 * be removed by the caller.
 */
export const resolvePendingSlide = async (
  slideEl: HTMLElement
): Promise<boolean> => {
  const {
    resolveType,
    resolveHandle,
    resolveManualLabel,
    resolveRenderTitle,
    resolveRenderDetails
  } = slideEl.dataset;
  if (!resolveHandle) return false;

  const manualLabel = resolveManualLabel ?? '';
  const client = await getSharedStorefrontClient();
  const country = getCountryCode();

  if (resolveType === 'collection') {
    const { data } = await client.query({
      query: carouselCollectionByHandleQuery,
      variables: { handle: resolveHandle, country }
    });
    const collection = data.collectionByHandle;
    // Mirrors sections/category-carousel.liquid's two-tier fallback: a
    // collection's own image wins, otherwise fall back to its first
    // product's featured image — without this, collections that rely on
    // the product-image fallback (common; a collection image isn't
    // required) would be wrongly treated as invalid and removed.
    const image =
      collection?.image ?? collection?.products.nodes[0]?.featuredImage;
    if (!image) return false;
    populateImage(slideEl, image.url, image.altText ?? '');
    // Collections never auto-derive a title in Liquid (product-capture-title
    // only ever runs for products) — a manual label is the only source.
    if (resolveRenderTitle === 'true' && manualLabel) {
      populateTitle(slideEl, manualLabel);
    }
    return true;
  }

  const { data } = await client.query({
    query: carouselProductByHandleQuery,
    variables: { handle: resolveHandle, country }
  });
  const product = data.productByHandle;
  if (!product?.featuredImage) return false;

  populateImage(
    slideEl,
    product.featuredImage.url,
    product.featuredImage.altText ?? ''
  );
  if (resolveRenderTitle === 'true') {
    populateTitle(slideEl, resolveTitle(product, manualLabel));
  }
  if (resolveRenderDetails === 'true') {
    try {
      // resolveTag() needs the i18n instance for the yarn-weight tag label —
      // setupI18n is memoized, so this is a no-op if something else (e.g. a
      // mounted Vue app) already initialized it on this page. Isolated in its
      // own try/catch: a details/i18n failure shouldn't discard the image
      // and title that already resolved successfully above.
      await setupI18n(window.Shopify.locale);
      populateDetails(slideEl, product);
    } catch (error) {
      console.error(error);
    }
  }
  return true;
};

export { carouselResolveDataset };
